import React, { useState, useEffect } from 'react';
import { Card, Spin, Table, Input, Button, DatePicker, Radio, Select, Drawer, Space } from 'antd';
import {
  GiftOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReportService from '../../../services/ReportService';

const { RangePicker } = DatePicker;
const { Option } = Select;

const GiftReports = () => {
  const [loading, setLoading] = useState(true);
  const [dateFilterType, setDateFilterType] = useState('quick');
  const [dateFilter, setDateFilter] = useState('today');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [searchText, setSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftDateFilterType, setDraftDateFilterType] = useState('quick');
  const [draftDateFilter, setDraftDateFilter] = useState('today');
  const [draftSelectedDate, setDraftSelectedDate] = useState(dayjs());
  const [draftDateRange, setDraftDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [draftSelectedBranch, setDraftSelectedBranch] = useState(null);

  const [ikramTutari, setIkramTutari] = useState([]);
  const [ikramTutariLoading, setIkramTutariLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const token = localStorage.getItem("token") || localStorage.getItem("x-access-token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const companyCode = userDetails?.CompanyCode || null;

  useEffect(() => {
    const loadBranches = async () => {
      if (!companyCode) {
        setBranches([]);
        return;
      }
      try {
        const response = await ReportService.company.getBranches({
          token,
          CompanyCode: companyCode
        });
        if (response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
          setBranches(data);
        }
      } catch (error) {
        console.error('Error loading branches:', error);
        setBranches([]);
      }
    };
    loadBranches();
  }, [token, companyCode]);

  const getDateParams = (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null) => {
    const currentFilter = filterOverride !== null ? filterOverride : dateFilter;
    const currentFilterType = filterTypeOverride !== null ? filterTypeOverride : dateFilterType;
    const currentDateRange = dateRangeOverride !== null ? dateRangeOverride : dateRange;
    const currentSelectedDate = selectedDateOverride !== null ? selectedDateOverride : selectedDate;

    if (currentFilterType === 'range' && currentDateRange && currentDateRange[0] && currentDateRange[1]) {
      return {
        StartDate: currentDateRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentDateRange[1].add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    if (currentFilterType === 'quick' && currentFilter === 'custom') {
      return {
        StartDate: currentSelectedDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentSelectedDate.add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    return {
      DateFilter: currentFilter
    };
  };

  const fetchIkramTutari = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null) => {
    if (!companyCode || companyCode.trim() === '') {
      setIkramTutari([]);
      return;
    }
    try {
      setIkramTutariLoading(true);
      const dateParams = getDateParams(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride);
      const response = await ReportService.instant.getBranchGiftReport({
        token,
        CompanyCode: companyCode,
        BranchCode: branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || undefined),
        ...dateParams
      });

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
        setIkramTutari(data);
      } else {
        setIkramTutari([]);
      }
    } catch (error) {
      console.error('Error fetching ikram tutarı:', error);
      setIkramTutari([]);
    } finally {
      setIkramTutariLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchIkramTutari();
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    setDateFilter(newFilter);
    setDateFilterType('quick');
    if (newFilter !== 'custom') {
      fetchIkramTutari(newFilter, 'quick', null, null, selectedBranch);
    }
  };

  const handleCustomDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      setDateFilter('custom');
      fetchIkramTutari('custom', 'quick', null, date, selectedBranch);
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      fetchIkramTutari(null, 'range', dates, null, selectedBranch);
    }
  };

  const handleDateFilterTypeChange = (e) => {
    const newType = e.target.value;
    setDateFilterType(newType);
    if (newType === 'range') {
      fetchIkramTutari(null, 'range', dateRange, null, selectedBranch);
    } else {
      fetchIkramTutari(dateFilter, 'quick', null, selectedDate, selectedBranch);
    }
  };

  const handleBranchChange = (value) => {
    setSelectedBranch(value);
    fetchIkramTutari(dateFilter, dateFilterType, dateRange, selectedDate, value);
  };

  const openFilters = () => {
    setDraftDateFilterType(dateFilterType);
    setDraftDateFilter(dateFilter);
    setDraftSelectedDate(selectedDate);
    setDraftDateRange(dateRange);
    setDraftSelectedBranch(selectedBranch);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setDateFilterType(draftDateFilterType);
    setDateFilter(draftDateFilter);
    setSelectedDate(draftSelectedDate);
    setDateRange(draftDateRange);
    setSelectedBranch(draftSelectedBranch);
    setIsFilterOpen(false);

    if (draftDateFilterType === 'range') {
      fetchIkramTutari(null, 'range', draftDateRange, null, draftSelectedBranch);
    } else if (draftDateFilter === 'custom') {
      fetchIkramTutari('custom', 'quick', null, draftSelectedDate, draftSelectedBranch);
    } else {
      fetchIkramTutari(draftDateFilter, 'quick', null, null, draftSelectedBranch);
    }
  };

  const clearFilters = () => {
    const defaultDateFilterType = 'quick';
    const defaultDateFilter = 'today';
    const defaultSelectedDate = dayjs();
    const defaultDateRange = [dayjs().subtract(7, 'day'), dayjs()];
    const defaultSelectedBranch = null;

    setDraftDateFilterType(defaultDateFilterType);
    setDraftDateFilter(defaultDateFilter);
    setDraftSelectedDate(defaultSelectedDate);
    setDraftDateRange(defaultDateRange);
    setDraftSelectedBranch(defaultSelectedBranch);

    setDateFilterType(defaultDateFilterType);
    setDateFilter(defaultDateFilter);
    setSelectedDate(defaultSelectedDate);
    setDateRange(defaultDateRange);
    setSelectedBranch(defaultSelectedBranch);

    fetchIkramTutari(defaultDateFilter, defaultDateFilterType, null, null, null);
  };

  const isToday = () => {
    if (dateFilterType === 'range') {
      return false;
    }
    if (dateFilter === 'custom') {
      const selected = selectedDate;
      const today = dayjs();
      return selected.format('YYYY-MM-DD') === today.format('YYYY-MM-DD');
    }
    return dateFilter === 'today';
  };

  const getReportTitle = (baseTitle) => {
    if (isToday()) {
      return `Anlık ${baseTitle}`;
    }
    return `Toplam ${baseTitle}`;
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const calculateTotalAmount = (data, key) => {
    return data.reduce((sum, row) => sum + parseFloat(row[key] || 0), 0);
  };

  const ikramColumns = [
    {
      title: <span className="text-base">Şube Adı</span>,
      dataIndex: 'SubeAdi',
      key: 'SubeAdi',
      width: isMobile ? '35%' : '40%',
      render: (text) => <span className="text-base">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className="text-base">İkram Edilen Ürün Sayısı</span>,
      dataIndex: 'IkramEdilenUrunSayisi',
      key: 'IkramEdilenUrunSayisi',
      width: isMobile ? '30%' : '30%',
      render: (value) => (
        <span className="text-base font-semibold text-blue-600">
          {formatNumber(value || 0)} adet
        </span>
      ),
      sorter: (a, b) => (a.IkramEdilenUrunSayisi || 0) - (b.IkramEdilenUrunSayisi || 0),
    },
    {
      title: <span className="text-base">İkram Tutarı</span>,
      dataIndex: 'IkramTutari',
      key: 'IkramTutari',
      width: isMobile ? '35%' : '30%',
      render: (value) => (
        <span className="text-base font-semibold text-purple-600">
          {formatNumber(value || 0)} ₺
        </span>
      ),
      sorter: (a, b) => (a.IkramTutari || 0) - (b.IkramTutari || 0),
    },
  ];

  const filterData = (data) => {
    if (!searchText) return data;
    const needle = searchText.toLowerCase();
    return data.filter(row =>
      String(row.SubeAdi || '').toLowerCase().includes(needle) ||
      String(row.SubeKodu || '').toLowerCase().includes(needle)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 shadow-lg backdrop-blur-sm mb-4">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-pink-500/5 to-rose-500/5"></div>
        <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg sm:rounded-xl shadow-lg">
              <GiftOutlined className="text-white text-lg sm:text-xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent leading-tight">
                İkram Raporu
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">
                Şube bazlı ikram raporları
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchIkramTutari(dateFilter, dateFilterType, dateRange, selectedDate, selectedBranch)}
              loading={ikramTutariLoading}
            >
              Yenile
            </Button>
            <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <Input
          allowClear
          placeholder="Şube ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="large"
          className="flex-1 rounded-lg"
        />
      </div>

      {/* İkram Raporu */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <GiftOutlined className="text-purple-500" />
            <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>
              {getReportTitle('İkram')}
            </h2>
          </div>
        }
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white"
        styles={{ body: { padding: isMobile ? 12 : 18 } }}
      >
        <Table
          columns={ikramColumns}
          dataSource={filterData(ikramTutari)}
          loading={ikramTutariLoading}
          rowKey="SubeKodu"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} şube`,
          }}
          size={isMobile ? 'small' : 'middle'}
          summary={(pageData) => {
            const totalUrunSayisi = calculateTotalAmount(pageData, 'IkramEdilenUrunSayisi');
            const totalTutar = calculateTotalAmount(pageData, 'IkramTutari');
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-50">
                  <Table.Summary.Cell index={0}>
                    <span className="font-bold text-base">Genel Toplam</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <span className="font-bold text-base text-blue-600">{formatNumber(totalUrunSayisi)} adet</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <span className="font-bold text-base text-purple-600">{formatNumber(totalTutar)} ₺</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Filtre Tipi</span>
            <Radio.Group
              value={draftDateFilterType}
              onChange={(e) => setDraftDateFilterType(e.target.value)}
              className="w-full"
            >
              <Radio.Button value="quick" className="w-1/2 text-center">Gün</Radio.Button>
              <Radio.Button value="range" className="w-1/2 text-center">Tarih Aralığı</Radio.Button>
            </Radio.Group>
          </div>

          {draftDateFilterType === 'quick' ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Gün Seçimi</span>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Bugün', value: 'today' },
                    { label: 'Dün', value: 'yesterday' },
                    { label: 'Gün Seçin', value: 'custom' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setDraftDateFilter(option.value)}
                      className={`
                        px-3 py-2 text-sm font-semibold transition-all duration-300 ease-out rounded-lg
                        ${draftDateFilter === option.value
                          ? 'bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg'
                          : 'text-slate-600 hover:text-slate-800 hover:bg-gray-100 border border-gray-200'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              {draftDateFilter === 'custom' && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-slate-500">Özel Gün</span>
                  <DatePicker
                    value={draftSelectedDate}
                    onChange={(date) => setDraftSelectedDate(date)}
                    format="DD.MM.YYYY"
                    className="w-full"
                    placeholder="Gün seçin"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">Tarih Aralığı</span>
              <RangePicker
                value={draftDateRange}
                onChange={(dates) => setDraftDateRange(dates || [])}
                format="DD.MM.YYYY"
                className="w-full"
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Şube</span>
            <Select
              value={draftSelectedBranch}
              onChange={(value) => setDraftSelectedBranch(value)}
              placeholder="Şube seçiniz"
              className="w-full"
              allowClear
            >
              {branches.map((branch) => (
                <Select.Option key={branch.SubeKodu} value={branch.SubeKodu}>
                  {branch.SubeAdi}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default GiftReports;

